import { useEffect, useState, useCallback } from 'react'
import { Wifi, WifiOff, Upload, Check, AlertCircle, RefreshCw } from 'lucide-react'
import { surveySyncEngine } from '../../lib/services/surveySync'

export function SyncStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [pending, setPending] = useState(0)
  const [failed, setFailed] = useState(0)
  const [terminalFailed, setTerminalFailed] = useState(0)
  const [retrying, setRetrying] = useState(false)

  const refreshCounts = useCallback(async () => {
    const [p, f, t] = await Promise.all([
      surveySyncEngine.pendingCount(),
      surveySyncEngine.failedCount(),
      surveySyncEngine.terminalFailedCount(),
    ])
    setPending(p)
    setFailed(f)
    setTerminalFailed(t)
  }, [])

  useEffect(() => {
    const goOnline = () => setIsOnline(true)
    const goOffline = () => setIsOnline(false)
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
    }
  }, [])

  useEffect(() => {
    let mounted = true
    const poll = async () => {
      const [p, f, t] = await Promise.all([
        surveySyncEngine.pendingCount(),
        surveySyncEngine.failedCount(),
        surveySyncEngine.terminalFailedCount(),
      ])
      if (mounted) {
        setPending(p)
        setFailed(f)
        setTerminalFailed(t)
      }
    }
    poll()
    const timer = setInterval(poll, 5_000)
    return () => { mounted = false; clearInterval(timer) }
  }, [])

  const handleRetry = useCallback(async () => {
    setRetrying(true)
    try {
      await surveySyncEngine.retryAll()
      await refreshCounts()
    } finally {
      setRetrying(false)
    }
  }, [refreshCounts])

  return (
    <div className="flex items-center gap-2">
      {isOnline ? (
        <Wifi size={14} className="text-emerald-500" />
      ) : (
        <WifiOff size={14} className="text-red-500" />
      )}

      {terminalFailed > 0 ? (
        <span className="flex items-center gap-1 text-xs font-semibold text-red-600">
          <AlertCircle size={12} />
          {terminalFailed} need recapture
        </span>
      ) : failed > 0 ? (
        <button
          type="button"
          onClick={handleRetry}
          disabled={retrying}
          className="flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
        >
          {retrying ? (
            <RefreshCw size={12} className="animate-spin" />
          ) : (
            <AlertCircle size={12} />
          )}
          {retrying ? 'Retrying…' : `${failed} failed — tap to retry`}
        </button>
      ) : pending > 0 ? (
        <span className="flex items-center gap-1 text-xs font-semibold text-amber-600">
          <Upload size={12} />
          {pending} pending
        </span>
      ) : (
        <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600">
          <Check size={12} />
          Synced
        </span>
      )}
    </div>
  )
}
